import { observable, action, observe, computed, makeObservable } from "mobx";
import _ from "lodash";
import React from "react";

import authStore from "./authStore";
import MessagingStore from "./Widgets/Messaging/MessagingStore";
import { fetchVersion, getSites } from "./serviceCalls";
import documentLabels from "./Widgets/Monitor/documentLabels";
import config from "./config";
import { MODULE_ID } from "./shared/moduleIds";
import { ColorSchemeStore } from "./shared/colorScheme";
import ClientPrefsStore from "./stores/ClientPrefsStore";

export default class AppStore {
  patientStore = undefined;
  patientListConfigStore = undefined;
  patient = undefined;
  history = window.history;
  authStore = authStore;
  userMenuOpen;
  hideMainNav;
  menuOpen; // mobile
  api;
  documentLabels = documentLabels;
  messagingStore = new MessagingStore();
  vendorLoginRequired = true;
  colorScheme;
  prefsStore;
  showPatientHeader;

  disposers = new Set();
  concludeVendorCreds = () => {
    this.vendorLoginRequired = false;
  };

  constructor() {
    makeObservable(this, {
      patientStore: observable,
      patientListConfigStore: observable,
      patient: observable,
      history: observable,
      userMenuOpen: observable,
      showPatientHeader: observable,
      prefs: computed,
      prefsFetched: computed,
      prefsPromise: computed,
      hasMultiPatient: false,
      prefsStore: observable,
      hideMainNav: observable,
      menuOpen: observable,
      api: observable,
      documentLabels: observable,
      messaging: computed,
      concludeVendorCreds: action,
      vendorLoginRequired: observable,
      colorScheme: observable,
      darkMode: computed,
      fetchSites: action,
    });

    authStore.appStore = this;
    this.prefsStore = new ClientPrefsStore({ appStore: this });
    if (authStore.authToken) {
      this.prefsStore.fetchPrefs();
      this.documentLabels.fetch();
    }
    const dispose = observe(authStore, () => {
      if (authStore.authToken) {
        this.prefsStore.fetchPrefs();
        this.documentLabels.fetch();
        this.fetchSites();
        dispose();
      }
    });
    this.fetchSites();
    this.fetchVersion();
    this.colorScheme = new ColorSchemeStore({ appStore: this });
  }

  // getters
  get prefs() {
    return this.prefsStore.prefs;
  }
  get serverPrefs() {
    return this.prefsStore.prefs?.userPreferences?.server;
  }
  get prefsPromise() {
    return this.prefsStore.prefsPromise;
  }
  get prefsFetched() {
    return this.prefsStore.prefsFetched;
  }
  setPref = (...args) => this.prefsStore.setPref(...args);
  getPref = (...args) => this.prefsStore.getPref(...args);
  fetchPrefs = (...args) => this.prefsStore.fetchPrefs(...args);

  get messaging() {
    return (
      !this.messagingStore.isDisabled &&
      this.prefs?.userPreferences?.server?.messaging?.messagingEnabled
    );
  }

  get darkMode() {
    return this.colorScheme.darkMode;
  }

  get mayApproveSnippet() {
    return (
      this.prefs?.userPreferences?.server?.pm?.privileges?.includes(
        "APPROVE_SNIPPET",
      ) && !this.prefs.userPreferences.client?.pm?.preventSnippetAutoApproval
    );
  }

  get hasMultiPatient() {
    let moduleId = this.patientStore?.moduleId;
    if (moduleId === MODULE_ID.OBSTETRICS) {
      return (
        _.get(config, "ob.multiPatientOverride") ||
        _.get(this.prefs, "userPreferences.server.ob.privileges", []).includes(
          "ACCESS_MULTIVIEW",
        )
      );
    } else {
      return (
        _.get(config, "pm.multiPatientOverride") ||
        _.get(this.prefs, "userPreferences.server.pm.privileges", []).includes(
          "ACCESS_MULTIVIEW",
        )
      );
    }
  }
  get hasMultiday() {
    return Promise.resolve(true);
  }

  // actions
  fetchVersion() {
    this.versionPromise = fetchVersion().then((res) => (this.api = res));
  }
  fetchSites = async () => {
    if (!authStore.authToken || !config.ENABLE_SITE_SWITCHER) return;
    const { sites } = await getSites();
    if (sites) {
      sessionStorage.sites = JSON.stringify(sites);
    }
    return sites;
  };
}

/* Store helpers 
  see https://codingislove.com/setup-mobx-react-context/ for usage.
*/
export const StoreContext = React.createContext();

export const StoreProvider = ({ children, store }) => {
  return (
    <StoreContext.Provider value={store}>{children}</StoreContext.Provider>
  );
};

/* Hook to use store in any functional component */
export const useStore = () => React.useContext(StoreContext);

/* HOC to inject store to any functional or class component */
export const withStore = (Component) => (props) => {
  return <Component {...props} store={useStore()} />;
};
